import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useLocation } from 'react-router-dom'
import { useAskLupe } from '@/hooks/useAskLupe'
import { historyFor } from '@/lib/compute/universe'
import type { ChatTurn } from '@/lib/types/universe'

/** The Lupe page's route. */
export const LUPE_PATH = '/'

/** Pre-per-user key; the provider drops it once so another user's thread never resurfaces. */
export const LEGACY_THREAD_KEY = 'lupe.thread'
export const threadKey = (userId: string) => `${LEGACY_THREAD_KEY}:${userId}`
export const MAX_TURNS = 30

const cap = (turns: ChatTurn[]) => turns.slice(-MAX_TURNS)

/** The user's saved thread; no user or anything unreadable starts fresh. A question cut off by a reload becomes a retryable error. */
export function loadThread(userId: string | null | undefined): ChatTurn[] {
  if (!userId) return []
  try {
    const parsed: unknown = JSON.parse(
      sessionStorage.getItem(threadKey(userId)) ?? '[]'
    )
    if (!Array.isArray(parsed)) return []
    const turns = parsed as ChatTurn[]
    return turns[turns.length - 1]?.role === 'user'
      ? [
          ...turns,
          {
            role: 'assistant',
            text: '',
            error: 'Lupe did not finish answering',
          },
        ]
      : turns
  } catch {
    return []
  }
}

/** One Lupe conversation, persisted per tab. Only one question is in flight at a time. Mount once, via `LupeThreadProvider`. */
export function useLupeThreadState() {
  const { userId } = useAuth()
  const [owner, setOwner] = useState(userId)
  const [turns, setTurns] = useState(() => loadThread(userId))
  const ownerRef = useRef(userId)
  const onLupe = useLocation().pathname === LUPE_PATH
  const onLupeRef = useRef(onLupe)
  // An answer that settled while the user was elsewhere, until they visit Lupe.
  const [unseen, setUnseen] = useState(false)
  if (onLupe && unseen) setUnseen(false)
  const { mutateAsync } = useAskLupe()
  // Own pending state: StrictMode remounts the mutation observer, so `isPending` can stay true forever.
  const [pending, setPending] = useState(false)
  const inFlight = useRef(false)

  // A new user starts from their own thread; the previous user's request is abandoned.
  if (owner !== userId) {
    setOwner(userId)
    setTurns(loadThread(userId))
    setPending(false)
    setUnseen(false)
  }

  useEffect(() => {
    ownerRef.current = userId
    inFlight.current = false
  }, [userId])

  useEffect(() => {
    onLupeRef.current = onLupe
  }, [onLupe])

  useEffect(() => {
    if (!userId) return
    try {
      sessionStorage.setItem(threadKey(userId), JSON.stringify(cap(turns)))
    } catch {
      // Storage full or blocked: the thread still works for this page view.
    }
  }, [turns, userId])

  const ask = useCallback(
    async (base: ChatTurn[], message: string) => {
      if (inFlight.current) return
      inFlight.current = true
      const asker = ownerRef.current
      setPending(true)
      setTurns(cap([...base, { role: 'user', text: message }]))
      let answer: ChatTurn
      try {
        const reply = await mutateAsync({
          message,
          history: historyFor(base, message),
        })
        answer = { role: 'assistant', text: reply.text, reply }
      } catch (err) {
        const error = err instanceof Error ? err.message : 'Lupe is unavailable'
        answer = { role: 'assistant', text: '', error }
      }
      if (ownerRef.current !== asker) return
      inFlight.current = false
      setPending(false)
      setTurns((prev) => cap([...prev, answer]))
      if (!onLupeRef.current) setUnseen(true)
    },
    [mutateAsync]
  )

  const send = useCallback(
    (text: string) => {
      const message = text.trim()
      if (message) void ask(turns, message)
    },
    [ask, turns]
  )

  /** Drops the failed exchange and asks the last question again. */
  const retry = useCallback(() => {
    const i = turns.map((t) => t.role).lastIndexOf('user')
    if (i >= 0) void ask(turns.slice(0, i), turns[i].text)
  }, [ask, turns])

  const clear = useCallback(() => setTurns([]), [])

  const answeringElsewhere = pending && !onLupe

  return useMemo(
    () => ({ turns, pending, answeringElsewhere, unseen, send, retry, clear }),
    [turns, pending, answeringElsewhere, unseen, send, retry, clear]
  )
}

export type LupeThread = ReturnType<typeof useLupeThreadState>

export const LupeThreadContext = createContext<LupeThread | null>(null)

/** The app-wide Lupe conversation; it outlives the Lupe page so answers survive navigation. */
export function useLupeThread(): LupeThread {
  const thread = useContext(LupeThreadContext)
  if (!thread)
    throw new Error('useLupeThread must be used inside <LupeThreadProvider>')
  return thread
}
